import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { IconType } from 'react-icons';
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaWhatsapp } from 'react-icons/fa6';

import { cn } from '@mds/shared/lib/utils';

import { FOOTER_COLUMNS, SITE, SOCIAL, type SiteLink } from '@/data';
import { spaceQueries } from '@/features/spaces/spaces.queries';

/**
 * The glyph for each account id.
 *
 * react-icons rather than lucide for these four: lucide dropped its brand
 * marks, and WhatsApp — the channel people here actually use — is not one to
 * quietly swap for a generic speech bubble. The map lives here and not in the
 * data because picking an icon is a rendering decision; `@/data/site` only
 * says which accounts exist.
 */
const SOCIAL_ICON: Record<(typeof SOCIAL)[number]['id'], IconType> = {
  instagram: FaInstagram,
  facebook: FaFacebookF,
  linkedin: FaLinkedinIn,
  whatsapp: FaWhatsapp,
};

/**
 * Focus takes the bright ring for the same reason admin's sidebar does: on the
 * panel the dark mint is 4.5:1 and the bright one is 14:1.
 *
 * No radius in here on purpose. `cn` is tailwind-merge, so a `rounded-*` in
 * this string would beat the one the call site set — it comes last — and the
 * round icon buttons would come out as squares. Each caller states its own.
 */
const focusRing =
  'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus-on-panel';

const quietLink = cn(
  'rounded-sm text-(--footer-link-fg) transition-colors duration-(--duration-fast)',
  'ease-standard hover:text-on-panel',
  focusRing,
);

export default function SiteFooter() {
  // Every space the API lists, under its own name and slug, so the column can
  // never point at a page that does not exist. The same query the spaces page
  // and the home rail read, so on those it costs no request of its own. Until
  // it lands, or if it fails, the column offers the spaces page alone.
  const spaces = useQuery(spaceQueries.list());
  const columns: { heading: string; items: SiteLink[] }[] = [
    {
      heading: 'Spaces',
      items: spaces.data?.length
        ? spaces.data.map((space) => ({ label: space.name, to: `/spaces/${space.slug}` }))
        : [{ label: 'All spaces', to: '/spaces' }],
    },
    ...FOOTER_COLUMNS,
  ];

  return (
    // Rounded at the top only, so the panel reads as a plane the page slides
    // under rather than as the last section on it.
    // `mt-section`: the gap above the footer is the footer's, not each page's
    // last band's. The token is 40px on a phone and 72px from `sm`.
    <footer data-ground="panel" className="mt-section rounded-t-card bg-panel text-on-panel">
      <div className="mx-auto flex max-w-page flex-col gap-block px-gutter pt-section pb-block">
        {/* One row: the logo with the social accounts under it, then the link
            columns, the last of which is how to reach a person. `flex-wrap`
            with a basis on the columns is what lays them side by side on a
            desktop and stacks them on a phone without a breakpoint. */}
        <div className="flex flex-wrap items-start gap-x-28 gap-y-10">
          <div className="flex shrink-0 flex-col gap-8">
            <Link to="/" className={cn('shrink-0 rounded-sm', focusRing)}>
              {/* The white-lettered mark, trimmed of the source file's padding so
                it lines up with the icons under it. */}
              <img
                src="/images/mds-logo-on-dark.png"
                alt={`${SITE.name}, ${SITE.fullName}`}
                className="h-14 w-auto"
              />
            </Link>

            <ul className="flex list-none flex-wrap gap-2.5">
              {SOCIAL.map(({ id, label, href }) => {
                // An account with no known address is left out rather than
                // linked somewhere it does not belong.
                if (!href) return null;
                const Icon = SOCIAL_ICON[id];
                return (
                  <li key={id}>
                    {/* size-11 is the 44px hit target rather than the design's
                      42px — the 2px is what takes it over the WCAG 2.2 floor.
                      A new tab: these leave the site. */}
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={label}
                      className={cn(
                        'inline-flex size-11 items-center justify-center rounded-button border',
                        'border-(--footer-icon-edge) transition-all duration-(--duration-fast)',
                        'ease-standard hover:-translate-y-0.5 hover:border-brand hover:bg-brand',
                        'hover:text-on-brand',
                        focusRing,
                      )}
                    >
                      <Icon className="size-4.5" aria-hidden="true" />
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          <nav
            aria-label="Footer"
            className="grid flex-1 basis-120 grid-cols-[repeat(auto-fit,minmax(8rem,1fr))] gap-x-6 gap-y-8"
          >
            {columns.map((column) => (
              <div key={column.heading} className="flex flex-col gap-2 text-sm">
                {/* Medium, not semibold: the mint colour already marks it as
                    the column's name, and semibold on the dark panel read heavy. */}
                <h2 className="mb-1 font-medium text-(--footer-eyebrow-fg)">{column.heading}</h2>
                <ul className="flex list-none flex-col gap-2">
                  {column.items.map((item) => (
                    <li key={item.label}>
                      {item.to ? (
                        <Link to={item.to} className={quietLink}>
                          {item.label}
                        </Link>
                      ) : item.href ? (
                        <a href={item.href} className={quietLink}>
                          {item.label}
                        </a>
                      ) : (
                        <span className="text-(--footer-link-fg)">{item.label}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        {/* The standard closing line: the year, the full name and its short
            form, and the rights reserved. Plain small text rather than the
            mono label style, since it is a sentence, not a label. */}
        <p className="m-0 border-t border-(--footer-hairline) pt-6 text-xs text-(--footer-link-fg)">
          © {new Date().getFullYear()} {SITE.fullName} ({SITE.name}). All rights reserved.
        </p>
      </div>
    </footer>
  );
}
